import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import EmptyCardAnalytics from "assets/images/EmptyCardAnalytics.png";
import EmptyCardGrowth from "assets/images/EmptyCardGrowth.png";
import EmptyCardPortfolioAnalysisOverview from "assets/images/EmptyCardPortfolioAnalysisOverview.png";
import { AsyncContentMui, RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard, Props as EmptyCardProps } from "components/Empty-card/EmptyCard";
import { DealCreate } from "components/modals";
import { QueryManager } from "components/reports";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useCanCreateDeal, useNavigate, usePrompt } from "hooks";
import {
  useAccountSelector,
  usePickUserPermissions,
  useReportsActions,
  useReportsFiltersSelector,
  useReportsQueriesSelector,
  useSelectBookmarksByType
} from "store";
import { IReportType, UserPermissionType } from "store/types";

import { checkReportType } from "../../components/guards/Default-view.guard";
import { RoleName } from "../../constants";
import { Routes } from "../../constants/routes";
import { CashflowModel, Snapshot, Timeline } from "../index";

type EmptyStateItem = Pick<EmptyCardProps, "title" | "description" | "image">;

const emptyStateConfig: Record<string, EmptyStateItem> = {
  [Routes.timeline]: {
    title: EMPTY_CARD_CONTENT.portfolioTimeline.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioTimeline.description,
      externalText: EMPTY_CARD_CONTENT.portfolioTimeline.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardGrowth, sx: { width: 315, height: 140 } }
  },
  [Routes.cashflow]: {
    title: EMPTY_CARD_CONTENT.portfolioCashflow.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioCashflow.description,
      externalText: EMPTY_CARD_CONTENT.portfolioCashflow.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardGrowth, sx: { width: 315, height: 140 } }
  },
  [Routes.snapshot]: {
    title: EMPTY_CARD_CONTENT.portfolioSnapshot.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioSnapshot.description,
      externalText: EMPTY_CARD_CONTENT.portfolioSnapshot.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardPortfolioAnalysisOverview, sx: { width: 640, height: 243 } }
  },
  [Routes.classic]: {
    title: EMPTY_CARD_CONTENT.portfolioClassic.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioClassic.description,
      externalText: EMPTY_CARD_CONTENT.portfolioClassic.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardAnalytics, sx: { width: 315, height: 140 } }
  }
};

interface PortfolioAnalysisProps {
  scopedInvestorIds?: number[];
}

export const PortfolioAnalysis: React.FC<PortfolioAnalysisProps> = ({ scopedInvestorIds }) => {
  const routeComponents: Record<string, React.ReactNode> = {
    [Routes.timeline]: <Timeline />,
    [Routes.cashflow]: <CashflowModel />,
    [Routes.snapshot]: <Snapshot />,
    [Routes.classic]: <Snapshot />
  };
  
  const reportsBookmarks = useSelectBookmarksByType("report");
  const portfolioAnalysis = usePickUserPermissions("portfolio_analysis");
  const noAccessPermission = portfolioAnalysis && portfolioAnalysis === UserPermissionType.NO_ACCESS;
  const canCreateDeal = useCanCreateDeal();
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { show_cashflow } = useAccountSelector() || {};
  const { pathname } = useLocation();

  const { getCheckMeasureDataAndDealsAction, setQueriesAction } = useReportsActions();
  const reportsFilters = useReportsFiltersSelector();
  const { filteredPortfolio } = useReportsQueriesSelector();

  const sanitizedScopedInvestors = useMemo<number[] | undefined>(() => {
    if (!scopedInvestorIds) {
      return undefined;
    }

    const normalized = scopedInvestorIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value)) as number[];

    return normalized.length ? normalized : undefined;
  }, [scopedInvestorIds]);

  const filtersRef = useRef(reportsFilters);
  const filteredPortfolioRef = useRef<boolean | undefined>(filteredPortfolio);
  const previousInvestorFilterRef = useRef<(number | string | boolean)[] | undefined>();
  const previousFilteredPortfolioRef = useRef<boolean | undefined>();
  const appliedScopeRef = useRef(false);

  useEffect(() => {
    filtersRef.current = reportsFilters;
  }, [reportsFilters]);

  useEffect(() => {
    filteredPortfolioRef.current = filteredPortfolio;
  }, [filteredPortfolio]);

  const restoreScopedFilters = useCallback(() => {
    const currentFilters = filtersRef.current || {};
    const { Investor: _ignoredInvestor, ...restFilters } = currentFilters;
    const restoredFilters =
      previousInvestorFilterRef.current && previousInvestorFilterRef.current.length
        ? { ...restFilters, Investor: previousInvestorFilterRef.current }
        : restFilters;

    setQueriesAction({
      filters: restoredFilters,
      filteredPortfolio: previousFilteredPortfolioRef.current ?? false
    });

    previousInvestorFilterRef.current = undefined;
    previousFilteredPortfolioRef.current = undefined;
  }, [setQueriesAction]);

  useEffect(() => {
    if (!sanitizedScopedInvestors) {
      if (appliedScopeRef.current) {
        restoreScopedFilters();
        appliedScopeRef.current = false;
      }
      return;
    }

    const currentFilters = filtersRef.current || {};

    if (areNumericArraysEqual(sanitizedScopedInvestors, currentFilters.Investor)) {
      return;
    }

    if (!appliedScopeRef.current) {
      previousInvestorFilterRef.current = Array.isArray(currentFilters.Investor)
        ? [...currentFilters.Investor]
        : currentFilters.Investor;
      previousFilteredPortfolioRef.current = filteredPortfolioRef.current;
    }

    const nextFilters = {
      ...currentFilters,
      Investor: [...sanitizedScopedInvestors]
    };

    setQueriesAction({
      filters: nextFilters,
      filteredPortfolio: true
    });

    appliedScopeRef.current = true;
  }, [sanitizedScopedInvestors, restoreScopedFilters, setQueriesAction]);

  useEffect(() => {
    return () => {
      if (appliedScopeRef.current) {
        restoreScopedFilters();
        appliedScopeRef.current = false;
      }
    };
  }, [restoreScopedFilters]);
  
  const {
    action: checkMeasureData,
    data: hasData,
    loading: checkLoading
  } = useActionHook(getCheckMeasureDataAndDealsAction);

  useEffect(() => {
    checkMeasureData();
  }, [pathname]);

  const handleAddDeal = useCallback(async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });
    if (!data) return;
    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt, navigate]);

  const addDealButton = canCreateDeal ? (
    <Button variant="contained" onClick={handleAddDeal} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
      Add Deal
    </Button>
  ) : undefined;

  const defaultBookmark = reportsBookmarks.find(
    (item) => item.is_default && checkReportType(item.report_type, IReportType.PORTFOLIO)
  );

  const to = defaultBookmark ? defaultBookmark.params.split("?")[0] : Routes.snapshot;

  const routes = [Routes.timeline, ...(show_cashflow ? [Routes.cashflow] : []), Routes.snapshot, Routes.classic];

  const queryManagerFiltersOverride = useMemo(
    () => (sanitizedScopedInvestors ? { Investor: sanitizedScopedInvestors } : undefined),
    [sanitizedScopedInvestors]
  );
  const shouldLockInvestorFilter = Boolean(sanitizedScopedInvestors);

  const renderRoute = (route: string) => {
    const config = emptyStateConfig[route];

    return (
      <Route key={route} path={route}>
        {hasData ? routeComponents[route] : <EmptyCard {...config} actionsElement={addDealButton} />}
      </Route>
    );
  };

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack
        sx={{
          alignItems: "stretch",
          flexGrow: 1,
          overflow: "hidden",
          bgcolor: "#f6f8fc"
        }}
      >
        <AsyncContentMui isLoading={checkLoading} spinnerProps={{ size: 150 }}>
          <QueryManager
            riskMetrics
            showFilters={Boolean(hasData)}
            filtersOverride={queryManagerFiltersOverride}
            hideInvestorFilter={shouldLockInvestorFilter}
            lockInvestorSelection={shouldLockInvestorFilter}
          />
          <Stack
            sx={{
              gap: "16px",
              overflow: "auto",
              flexGrow: 1,
              scrollMarginRight: "30px",
              pr: "2px",
              pl: "2px",
              marginX: "-2px"
            }}
          >
            <Switch>
              {/* מקור: הראוטים הקיימים לא משתנים */}
              {routes.map(renderRoute)}
              
              {/* הפתרון: Catch-all ראוט לשימוש כ-Embedded בתוך עמוד הלקוח */}
              <Route path="*">
                 {hasData ? (routeComponents[to] || <Snapshot />) : <EmptyCard {...(emptyStateConfig[to] || emptyStateConfig[Routes.snapshot])} actionsElement={addDealButton} />}
              </Route>
            </Switch>
          </Stack>
        </AsyncContentMui>
      </Stack>
    </RoleComponentGuard>
  );
};

const areNumericArraysEqual = (
  left: number[] | undefined,
  right: (number | string | boolean)[] | undefined
): boolean => {
  if (!left && !right) {
    return true;
  }

  if (!left || !Array.isArray(right)) {
    return false;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => Number(right[index]) === value);
};